import { useEffect, useState, useCallback, useMemo } from 'react';
import api from '../api/axios';
import { formatInr } from '../utils/formatInr';
import { generateInvoicePDF, generateInvoicePDFBlob } from '../utils/generateInvoicePDF';
import TableSearchInput from '../components/TableSearchInput';
import ColumnFilterInput from '../components/ColumnFilterInput';
import SearchableSelectWithAdd from '../components/SearchableSelectWithAdd';
import { textHaystackIncludes, matchesInvoiceEditorLine, normalizeSearch } from '../utils/textSearch';

const BASE_URL = process.env.REACT_APP_API_URL?.replace('/api', '') || 'http://localhost:5000';
const fmt = (d) => d ? new Date(d).toLocaleString('en-IN', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: false }) : '—';
const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';
const LABOUR_TYPES = ['Carpenter Charges', 'Electrician Charges', 'POP Charges', 'Glass Charges', 'Transportation', 'CNC Charges', 'Other'];

const MILESTONE_DISPLAY_ORDER = [10, 50, 35, 5];

const pdfIcon = (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
    <path d="M14 2v6h6" />
    <path d="M9 15h6" />
    <path d="M9 18h3" />
  </svg>
);

const whatsappIcon = (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M12.04 2C6.58 2 2.13 6.45 2.13 11.91c0 1.75.46 3.45 1.32 4.95L2 22l5.28-1.38a9.87 9.87 0 0 0 4.76 1.21h.01c5.46 0 9.91-4.45 9.91-9.91C21.96 6.45 17.51 2 12.04 2Zm0 18.16h-.01a8.2 8.2 0 0 1-4.18-1.14l-.3-.18-3.13.82.84-3.05-.2-.31a8.18 8.18 0 0 1-1.25-4.39c0-4.54 3.69-8.23 8.24-8.23 2.2 0 4.27.86 5.82 2.41a8.17 8.17 0 0 1 2.41 5.82c0 4.54-3.7 8.25-8.24 8.25Zm4.51-6.17c-.25-.12-1.46-.72-1.69-.8-.23-.08-.39-.12-.56.12-.17.25-.64.8-.79.96-.14.17-.29.19-.54.06-.25-.12-1.04-.38-1.98-1.22-.73-.65-1.23-1.46-1.37-1.71-.14-.25-.02-.38.11-.5.11-.11.25-.29.37-.43.12-.14.17-.25.25-.41.08-.17.04-.31-.02-.43-.06-.12-.56-1.35-.77-1.85-.2-.49-.41-.42-.56-.43h-.48c-.17 0-.43.06-.66.31-.23.25-.86.84-.86 2.04s.89 2.37 1.02 2.53c.12.17 1.76 2.69 4.26 3.77.6.26 1.06.41 1.42.53.6.19 1.14.16 1.57.1.48-.07 1.46-.6 1.67-1.18.21-.58.21-1.08.14-1.18-.06-.1-.23-.16-.48-.28Z"/>
  </svg>
);

const linkIcon = (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M10 13a5 5 0 0 0 7.07 0l2.83-2.83a5 5 0 0 0-7.07-7.07L11.5 4.43" />
    <path d="M14 11a5 5 0 0 0-7.07 0L4.1 13.83a5 5 0 0 0 7.07 7.07l1.33-1.33" />
  </svg>
);

/** One list for all clients / browsers on this machine (was per-client `tatsavi:invoiceDescHints:<id>`). */
const INVOICE_DESC_HINTS_GLOBAL_KEY = 'tatsavi:invoiceDescHints:global';
const INVOICE_DESC_HINTS_LEGACY_PREFIX = 'tatsavi:invoiceDescHints:';
const INVOICE_DESC_HINTS_MIGRATED_FLAG = 'tatsavi:invoiceDescHints:migrated-global-v1';

function parseHintsArray(raw) {
  if (!raw) return [];
  try {
    const arr = JSON.parse(raw);
    return Array.isArray(arr) ? arr.filter((s) => typeof s === 'string') : [];
  } catch {
    return [];
  }
}

/** Merge old per-client keys into global once, then remove legacy keys. */
function ensureInvoiceDescHintsGlobalMigration() {
  try {
    if (localStorage.getItem(INVOICE_DESC_HINTS_MIGRATED_FLAG) === '1') return;
    const legacyBuckets = [];
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (!k || !k.startsWith(INVOICE_DESC_HINTS_LEGACY_PREFIX)) continue;
      if (k === INVOICE_DESC_HINTS_GLOBAL_KEY || k === INVOICE_DESC_HINTS_MIGRATED_FLAG) continue;
      legacyBuckets.push(...parseHintsArray(localStorage.getItem(k)));
    }
    const existingGlobal = parseHintsArray(localStorage.getItem(INVOICE_DESC_HINTS_GLOBAL_KEY));
    const merged = mergeDedupeDescriptions(existingGlobal, legacyBuckets);
    localStorage.setItem(INVOICE_DESC_HINTS_GLOBAL_KEY, JSON.stringify(merged));
    for (let i = localStorage.length - 1; i >= 0; i--) {
      const k = localStorage.key(i);
      if (!k || !k.startsWith(INVOICE_DESC_HINTS_LEGACY_PREFIX)) continue;
      if (k === INVOICE_DESC_HINTS_GLOBAL_KEY || k === INVOICE_DESC_HINTS_MIGRATED_FLAG) continue;
      localStorage.removeItem(k);
    }
    localStorage.setItem(INVOICE_DESC_HINTS_MIGRATED_FLAG, '1');
  } catch {
    /* private mode / quota */
  }
}

function readInvoiceDescHintsLS() {
  try {
    ensureInvoiceDescHintsGlobalMigration();
    return parseHintsArray(localStorage.getItem(INVOICE_DESC_HINTS_GLOBAL_KEY));
  } catch {
    return [];
  }
}

function writeInvoiceDescHintsLS(hints) {
  try {
    ensureInvoiceDescHintsGlobalMigration();
    localStorage.setItem(INVOICE_DESC_HINTS_GLOBAL_KEY, JSON.stringify(hints));
  } catch {
    /* private mode / quota */
  }
}

/** Case-insensitive unique strings, sorted for stable dropdown order. */
function mergeDedupeDescriptions(...lists) {
  const seen = new Set();
  const out = [];
  for (const list of lists) {
    for (const s of list || []) {
      const t = String(s || '').trim();
      if (!t) continue;
      const k = t.toLowerCase();
      if (seen.has(k)) continue;
      seen.add(k);
      out.push(t);
    }
  }
  out.sort((a, b) => a.localeCompare(b));
  return out;
}

function splitTotalIntoMilestones(totalRupees) {
  const totalPaise = Math.round(Math.max(0, Number(totalRupees) || 0) * 100);
  if (totalPaise <= 0) return MILESTONE_DISPLAY_ORDER.map(() => 0);
  const out = [];
  let allocatedPaise = 0;
  for (let i = 0; i < MILESTONE_DISPLAY_ORDER.length; i++) {
    const pct = MILESTONE_DISPLAY_ORDER[i];
    const isLast = i === MILESTONE_DISPLAY_ORDER.length - 1;
    const sharePaise = isLast ? totalPaise - allocatedPaise : Math.floor((totalPaise * pct) / 100);
    out.push(Math.round(sharePaise) / 100);
    allocatedPaise += sharePaise;
  }
  return out;
}

function latestReceiptByMilestone(receipts) {
  return (receipts || []).reduce((acc, rec) => {
    if (rec?.milestonePct == null) return acc;
    const key = String(rec.milestonePct);
    const current = acc[key];
    if (!current || new Date(rec.receivedAt || 0) > new Date(current.receivedAt || 0)) acc[key] = rec;
    return acc;
  }, {});
}

function milestoneInvoiceForRow(row, quoteLabel) {
  const received = Number(row.received) || 0;
  const amount = Number(row.amount) || 0;
  const pending = Math.max(0, Math.round((amount - received) * 100) / 100);
  return {
    invoiceNumber: `${quoteLabel || 'Milestone'}-${row.milestonePct || 'payment'}`,
    title: row.title,
    invoiceDate: new Date().toISOString(),
    subTotal: pending,
    total: pending,
    items: [
      { itemName: 'Milestone amount', amount },
      { itemName: 'Received amount', amount: -received },
      { itemName: 'Pending amount', amount: pending },
    ],
  };
}

// ── Create Charge Modal ───────────────────────────────────────────────────────
function CreateChargeModal({ onClose, onSaved }) {
  const [form, setForm] = useState({ context: 'Client Invoices', option: 'Credit', type: 'Decimal (₹)', chargeName: '', labourType: '' });
  const [saving, setSaving] = useState(false);

  const iS = { width: '100%', padding: '7px 10px', border: '1px solid #E5E7EB', borderRadius: 8, fontSize: 12, outline: 'none', background: '#F9FAFB', fontFamily: 'inherit' };
  const lS = { display: 'block', fontSize: 11, fontWeight: 600, color: '#6B7280', marginBottom: 4 };

  const submit = async e => {
    e.preventDefault();
    setSaving(true);
    try {
      const r = await api.post('/invoice-charges', form);
      onSaved(r.data.data.charge);
      onClose();
    } catch (err) {
      alert(err.response?.data?.message || 'Failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1030, padding: 16 }}
      onClick={onClose}
    >
      <div
        style={{ background: '#fff', borderRadius: 12, width: '100%', maxWidth: 420, boxShadow: '0 20px 60px rgba(0,0,0,0.2)', padding: 20 }}
        onClick={e => e.stopPropagation()}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
          <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700 }}>Create Charge</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 18, cursor: 'pointer', color: '#9CA3AF' }}>×</button>
        </div>
        <form onSubmit={submit}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
            <div>
              <label style={lS}>Context</label>
              <div style={{ padding: '8px 12px', border: '1px solid #E5E7EB', borderRadius: 8, fontSize: 12, background: '#F3F4F6', color: '#6B7280' }}>
                Client Invoices
              </div>
            </div>
            <div>
              <label style={lS}>Option</label>
              <select style={iS} value={form.option} onChange={e => setForm(f => ({ ...f, option: e.target.value }))}>
                <option>Credit</option>
                <option>Debit</option>
              </select>
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
            <div>
              <label style={lS}>Type</label>
              <select style={iS} value={form.type} onChange={e => setForm(f => ({ ...f, type: e.target.value }))}>
                <option>Decimal (₹)</option>
                <option>Percentage (%)</option>
                <option>Labour</option>
              </select>
            </div>
            <div>
              <label style={lS}>Charge Name</label>
              <input style={iS} value={form.chargeName} onChange={e => setForm(f => ({ ...f, chargeName: e.target.value }))} required />
            </div>
          </div>
          {form.type === 'Labour' && (
            <div style={{ marginBottom: 12 }}>
              <label style={lS}>Labour Type</label>
              <select style={iS} value={form.labourType} onChange={e => setForm(f => ({ ...f, labourType: e.target.value }))}>
                <option value="">Select…</option>
                {LABOUR_TYPES.map(t => <option key={t}>{t}</option>)}
              </select>
            </div>
          )}
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', borderTop: '1px solid #F3F4F6', paddingTop: 14 }}>
            <button type="button" onClick={onClose} style={{ padding: '7px 16px', border: '1px solid #E5E7EB', borderRadius: 8, background: '#fff', fontSize: 12, cursor: 'pointer' }}>Cancel</button>
            <button type="submit" disabled={saving} style={{ padding: '7px 16px', background: '#976793', color: '#fff', border: 'none', borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
              {saving ? 'Saving…' : 'Create'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── Invoice Editor ────────────────────────────────────────────────────────────
function InvoiceMilestoneSection({
  clientId,
  project,
  client,
  latestQuote,
  paymentReceipts,
  receivedDrafts,
  setReceivedDrafts,
  onSaved,
}) {
  const [editingPct, setEditingPct] = useState(null);
  const [savingPct, setSavingPct] = useState(null);
  const [pdfLoadingPct, setPdfLoadingPct] = useState(null);
  const [linkLoadingPct, setLinkLoadingPct] = useState(null);
  const [whatsAppLoadingPct, setWhatsAppLoadingPct] = useState(null);
  const receiptByPct = useMemo(() => latestReceiptByMilestone(paymentReceipts), [paymentReceipts]);
  const amounts = useMemo(() => splitTotalIntoMilestones(latestQuote?.total), [latestQuote?.total]);
  const quoteLabel = String(latestQuote?.quoteCode || latestQuote?.title || 'Milestone').trim();
  const clientForPdf = client || project?.client || { name: 'Client' };
  const projectForPdf = project || null;
  const rows = useMemo(() => MILESTONE_DISPLAY_ORDER.map((pct, idx) => {
    const receipt = receiptByPct[String(pct)];
    const received = receipt ? Number(receipt.amount) || 0 : Number(String(receivedDrafts[String(pct)] || '0').replace(/,/g, '')) || 0;
    const amount = amounts[idx] || 0;
    const remaining = Math.max(0, Math.round((amount - received) * 100) / 100);
    return {
      _id: `${latestQuote?._id || 'active'}-${pct}`,
      title: `Milestone ${pct}%${pct === 10 ? ' (Design Initiation)' : ''}`,
      milestonePct: pct,
      amount,
      received,
      remaining,
      status: remaining <= 0 ? 'Received' : received > 0 ? 'Partial' : 'Pending',
      receipt,
    };
  }).filter((row) => row.amount > 0), [amounts, latestQuote?._id, receiptByPct, receivedDrafts]);
  const invoiceTotal = rows.reduce((sum, row) => sum + row.amount, 0);
  const receivedTotal = rows.reduce((sum, row) => sum + row.received, 0);
  const remainingTotal = Math.max(0, Math.round((invoiceTotal - receivedTotal) * 100) / 100);
  const collectionPct = invoiceTotal > 0 ? Math.min(100, Math.round((receivedTotal / invoiceTotal) * 100)) : 0;

  const saveReceivedAmount = async (row) => {
    const key = String(row.milestonePct);
    const amount = Number(String(receivedDrafts[key] ?? row.received ?? '').replace(/,/g, ''));
    if (!Number.isFinite(amount) || amount < 0) {
      alert('Enter a valid received amount.');
      return;
    }
    setSavingPct(key);
    try {
      const payload = {
        amount,
        milestonePct: row.milestonePct,
        note: `Milestone ${row.milestonePct}% received`,
      };
      if (row.receipt?._id) {
        await api.patch(`/clients/${clientId}/projects/${project._id}/payment-receipts/${row.receipt._id}`, payload);
      } else {
        await api.post(`/clients/${clientId}/projects/${project._id}/payment-receipts`, payload);
      }
      setEditingPct(null);
      await onSaved();
    } catch (e) {
      alert(e.response?.data?.message || 'Could not save received amount');
    } finally {
      setSavingPct(null);
    }
  };

  const createMilestonePdfBlob = async (row) =>
    generateInvoicePDFBlob(milestoneInvoiceForRow(row, quoteLabel), clientForPdf, projectForPdf);

  const downloadPdf = async (row) => {
    const key = String(row.milestonePct);
    setPdfLoadingPct(key);
    try {
      await generateInvoicePDF(milestoneInvoiceForRow(row, quoteLabel), clientForPdf, projectForPdf);
    } catch (e) {
      alert(e?.message || 'Could not generate invoice PDF');
    } finally {
      setPdfLoadingPct(null);
    }
  };

  const uploadPdfAndGetLink = async (row) => {
    const blob = await createMilestonePdfBlob(row);
    const invoice = milestoneInvoiceForRow(row, quoteLabel);
    const formData = new FormData();
    formData.append('pdf', blob, `${invoice.invoiceNumber}.pdf`);
    const res = await api.post(
      `/clients/${clientId}/projects/${project._id}/payment-receipts/whatsapp-pdf`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    );
    return res.data?.data || {};
  };

  const copyLink = async (row) => {
    const key = String(row.milestonePct);
    setLinkLoadingPct(key);
    try {
      const data = await uploadPdfAndGetLink(row);
      if (!data.pdfUrl) throw new Error('Missing invoice PDF link');
      await navigator.clipboard.writeText(data.pdfUrl);
      alert('Invoice link copied.');
    } catch (e) {
      alert(e.response?.data?.message || e.message || 'Could not copy invoice link');
    } finally {
      setLinkLoadingPct(null);
    }
  };

  const sendWhatsApp = async (row) => {
    const key = String(row.milestonePct);
    setWhatsAppLoadingPct(key);
    try {
      const data = await uploadPdfAndGetLink(row);
      const phoneDigits = String(data.phoneNumber || client?.phone || project?.client?.phone || '').replace(/\D/g, '');
      const phoneNumber = phoneDigits.length > 10 ? phoneDigits.slice(-10) : phoneDigits;
      if (!phoneNumber || !data.pdfUrl) throw new Error('Missing phone number or invoice PDF link');
      const clientLabel = data.clientName || client?.name || project?.client?.name || 'Client';
      const message = `Hello ${clientLabel}, please find your invoice attached:\n${data.pdfUrl}`;
      window.open(`https://wa.me/91${phoneNumber}?text=${encodeURIComponent(message)}`, '_blank', 'noopener,noreferrer');
    } catch (e) {
      alert(e.response?.data?.message || e.message || 'Could not prepare WhatsApp invoice');
    } finally {
      setWhatsAppLoadingPct(null);
    }
  };

  const th = { padding: '8px 10px', fontSize: 11, fontWeight: 600, color: '#6B7280', borderBottom: '1px solid #E5E7EB', textAlign: 'left', background: '#F9FAFB' };
  const td = { padding: '8px 10px', fontSize: 12, color: '#374151', borderBottom: '1px solid #F3F4F6', verticalAlign: 'middle' };
  const statBox = { border: '1px solid #E5E7EB', borderRadius: 8, padding: '10px 12px', background: '#fff', minWidth: 0 };
  const statLabel = { fontSize: 11, color: '#6B7280', marginBottom: 4 };
  const statValue = { fontSize: 15, fontWeight: 800, color: '#111827' };
  const iconBtn = (color, border, disabled) => ({
    width: 30,
    height: 30,
    border: `1px solid ${border}`,
    borderRadius: 6,
    background: '#fff',
    color,
    cursor: disabled ? 'wait' : 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    opacity: disabled ? 0.65 : 1,
  });

  if (!project?._id) return null;

  return (
    <div style={{ border: '1px solid #E5E7EB', borderRadius: 10, background: '#fff', marginBottom: 14, overflow: 'hidden' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, alignItems: 'baseline', padding: '12px 14px', borderBottom: '1px solid #E5E7EB', flexWrap: 'wrap' }}>
        <div>
          <h3 style={{ margin: 0, fontSize: 14, fontWeight: 700, color: '#1F2937' }}>Invoice milestones</h3>
          {latestQuote && <div style={{ marginTop: 3, fontSize: 11, color: '#6B7280' }}>Based on {latestQuote.title || latestQuote.quoteCode || 'latest quotation'}</div>}
        </div>
        {latestQuote && <strong style={{ fontSize: 13, color: '#111827' }}>Total: Rs. {formatInr(latestQuote.total || 0)}</strong>}
      </div>
      {!latestQuote ? (
        <div style={{ padding: '1.25rem', color: '#6B7280', fontSize: 13 }}>No active quotation found for invoice milestones.</div>
      ) : (
        <>
        <div style={{ padding: '12px 14px', borderBottom: '1px solid #E5E7EB' }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: 10 }}>
            <div style={statBox}>
              <div style={statLabel}>Invoice value</div>
              <div style={statValue}>Rs. {formatInr(invoiceTotal)}</div>
            </div>
            <div style={statBox}>
              <div style={statLabel}>Received</div>
              <div style={{ ...statValue, color: '#059669' }}>Rs. {formatInr(receivedTotal)}</div>
            </div>
            <div style={statBox}>
              <div style={statLabel}>Remaining</div>
              <div style={{ ...statValue, color: remainingTotal > 0 ? '#B45309' : '#059669' }}>Rs. {formatInr(remainingTotal)}</div>
            </div>
            <div style={statBox}>
              <div style={statLabel}>Collection</div>
              <div style={statValue}>{collectionPct}%</div>
              <div style={{ height: 5, borderRadius: 999, background: '#F3F4F6', marginTop: 7, overflow: 'hidden' }}>
                <div style={{ width: `${collectionPct}%`, height: '100%', background: '#976793' }} />
              </div>
            </div>
          </div>
        </div>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 1020 }}>
            <thead>
              <tr>
                <th style={th}>Milestone</th>
                <th style={{ ...th, width: 70 }}>%</th>
                <th style={{ ...th, width: 130, textAlign: 'right' }}>Amount</th>
                <th style={{ ...th, width: 170 }}>Received amount</th>
                <th style={{ ...th, width: 130, textAlign: 'right' }}>Remaining</th>
                <th style={{ ...th, width: 90, textAlign: 'center' }}>Status</th>
                <th style={{ ...th, width: 58, textAlign: 'center' }}>PDF</th>
                <th style={{ ...th, width: 58, textAlign: 'center' }}>Link</th>
                <th style={{ ...th, width: 88, textAlign: 'center' }}>WhatsApp</th>
                <th style={{ ...th, width: 90, textAlign: 'center' }}>Edit</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => {
                const key = String(row.milestonePct);
                const isEditing = editingPct === key || !row.receipt;
                return (
                  <tr key={row._id}>
                    <td style={td}>{row.title}</td>
                    <td style={td}>{row.milestonePct}%</td>
                    <td style={{ ...td, fontWeight: 700, textAlign: 'right' }}>Rs. {formatInr(row.amount)}</td>
                    <td style={td}>
                      <input
                        type="text"
                        inputMode="decimal"
                        value={isEditing ? (receivedDrafts[key] || '') : String(row.received || 0)}
                        onChange={(e) => setReceivedDrafts((prev) => ({ ...prev, [key]: e.target.value }))}
                        disabled={!isEditing}
                        placeholder="0"
                        style={{ width: 118, padding: '6px 8px', border: '1px solid #E5E7EB', borderRadius: 8, fontSize: 12, textAlign: 'right', background: isEditing ? '#fff' : '#F9FAFB', fontFamily: 'inherit' }}
                      />
                    </td>
                    <td style={{ ...td, fontWeight: 700, textAlign: 'right', color: row.remaining > 0 ? '#B45309' : '#059669' }}>Rs. {formatInr(row.remaining)}</td>
                    <td style={{ ...td, textAlign: 'center' }}>
                      <span style={{
                        display: 'inline-flex',
                        padding: '2px 8px',
                        borderRadius: 999,
                        fontSize: 10,
                        fontWeight: 700,
                        background: row.status === 'Received' ? '#D1FAE5' : row.status === 'Partial' ? '#FEF3C7' : '#F3F4F6',
                        color: row.status === 'Received' ? '#047857' : row.status === 'Partial' ? '#B45309' : '#6B7280',
                      }}>
                        {row.status}
                      </span>
                    </td>
                    <td style={{ ...td, textAlign: 'center' }}>
                      <button type="button" title="Download PDF" onClick={() => downloadPdf(row)} disabled={pdfLoadingPct === key} style={iconBtn('#DC2626', '#FECACA', pdfLoadingPct === key)}>{pdfIcon}</button>
                    </td>
                    <td style={{ ...td, textAlign: 'center' }}>
                      <button type="button" title="Copy link" onClick={() => copyLink(row)} disabled={linkLoadingPct === key} style={iconBtn('#976793', '#E9D5FF', linkLoadingPct === key)}>{linkIcon}</button>
                    </td>
                    <td style={{ ...td, textAlign: 'center' }}>
                      <button type="button" title="Send WhatsApp" onClick={() => sendWhatsApp(row)} disabled={whatsAppLoadingPct === key} style={iconBtn('#16A34A', '#BBF7D0', whatsAppLoadingPct === key)}>{whatsappIcon}</button>
                    </td>
                    <td style={{ ...td, textAlign: 'center' }}>
                      <button
                        type="button"
                        onClick={() => (isEditing ? saveReceivedAmount(row) : setEditingPct(key))}
                        disabled={savingPct === key}
                        style={{ padding: '6px 10px', border: '1px solid #976793', borderRadius: 8, background: isEditing ? '#976793' : '#fff', color: isEditing ? '#fff' : '#976793', fontSize: 11, fontWeight: 700, cursor: savingPct === key ? 'wait' : 'pointer' }}
                      >
                        {savingPct === key ? 'Saving...' : isEditing ? 'Save' : 'Edit'}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        </>
      )}
    </div>
  );
}

function InvoiceEditor({ invoiceId, clientId, projectId, invoiceType, onClose, onSaved }) {
  const [form, setForm] = useState({ title: '', invoiceDate: '', project: projectId || '', items: [], charges: [], termsAndConditions: '', remarks: '', bankAccount: '' });
  const [invoiceNumber, setInvoiceNumber] = useState('');
  const [saving, setSaving] = useState(false);
  const [openItemMenu, setOpenItemMenu] = useState(null);
  const [lineColFilters, setLineColFilters] = useState({ item: '' });
  /** Reusable description phrases (shared for all clients on this browser; localStorage global key). */
  const [descriptionHints, setDescriptionHints] = useState([]);
  /** Lets amount field show '' while editing; without this, clearing sets rate 0 and controlled value snaps back to "0". */
  const [amountDraftByTmpId, setAmountDraftByTmpId] = useState({});

  useEffect(() => {
    if (!invoiceId) {
      setForm((f) => ({ ...f, project: projectId || '' }));
      setDescriptionHints(mergeDedupeDescriptions(readInvoiceDescHintsLS(), []));
      return;
    }
    let cancelled = false;
    api.get('/clients/' + clientId + '/invoices/' + invoiceId).then((r) => {
      if (cancelled) return;
      const inv = r.data.data.invoice;
      setForm({
        title: inv.title || '',
        invoiceDate: inv.invoiceDate ? new Date(inv.invoiceDate).toISOString().split('T')[0] : '',
        project: inv.project?._id || inv.project || projectId || '',
        items: inv.items.map(it => ({ ...it, _tmpId: Math.random().toString(36).slice(2) })),
        charges: inv.charges || [],
        termsAndConditions: inv.termsAndConditions || '',
        remarks: inv.remarks || '',
        bankAccount: inv.bankAccount?._id || inv.bankAccount || '',
      });
      setInvoiceNumber(inv.invoiceNumber || '');
      const fromInv = (inv.items || []).map((it) => String(it.itemName || '').trim()).filter(Boolean);
      const merged = mergeDedupeDescriptions(readInvoiceDescHintsLS(), fromInv);
      writeInvoiceDescHintsLS(merged);
      setDescriptionHints(merged);
      setAmountDraftByTmpId({});
    }).catch(console.error);
    return () => { cancelled = true; };
  }, [invoiceId, clientId, projectId]);

  const descriptionHintOptions = useMemo(() => {
    const seen = new Set();
    const out = [];
    const push = (s) => {
      const t = String(s || '').trim();
      if (!t) return;
      const k = t.toLowerCase();
      if (seen.has(k)) return;
      seen.add(k);
      out.push(t);
    };
    descriptionHints.forEach(push);
    form.items.forEach((it) => push(it.itemName));
    return out.sort((a, b) => a.localeCompare(b));
  }, [descriptionHints, form.items]);

  const addDescriptionHint = useCallback((name) => {
    const n = String(name || '').trim();
    if (!n) return;
    setDescriptionHints((prev) => {
      const next = mergeDedupeDescriptions(prev, [n]);
      writeInvoiceDescHintsLS(next);
      return next;
    });
  }, []);

  useEffect(() => {
    const close = () => { setOpenItemMenu(null); };
    document.addEventListener('click', close);
    return () => document.removeEventListener('click', close);
  }, []);

  const calcAmount = (it) => {
    const base = (it.qty || 1) * (it.rate || 0);
    const disc = base * (it.discount || 0) / 100;
    const gstA = (base - disc) * (it.gst || 0) / 100;
    return Math.round((base - disc + gstA) * 100) / 100;
  };

  const subTotal = form.items.reduce((a, it) => a + calcAmount(it), 0);
  const gstTotal = form.items.reduce((a, it) => {
    const b = (it.qty || 1) * (it.rate || 0);
    const d = b * (it.discount || 0) / 100;
    return a + (b - d) * (it.gst || 0) / 100;
  }, 0);
  const total = subTotal + gstTotal;

  const displayedEditorItems = useMemo(
    () => form.items.filter((it) => matchesInvoiceEditorLine(it, lineColFilters)),
    [form.items, lineColFilters]
  );

  const addSimpleLine = () => {
    setForm((f) => ({
      ...f,
      items: [
        ...f.items,
        {
          _tmpId: Math.random().toString(36).slice(2),
          itemName: '',
          itemDetails: '',
          qty: 1,
          uom: 'unit',
          rate: 0,
          discount: 0,
          gst: 0,
          section: '',
          remarks: '',
        },
      ],
    }));
  };

  const updateItem = (tmpId, field, val) => setForm(f => ({ ...f, items: f.items.map(it => it._tmpId === tmpId ? { ...it, [field]: val } : it) }));
  const removeItem = (tmpId) => {
    setAmountDraftByTmpId((prev) => {
      if (!(tmpId in prev)) return prev;
      const next = { ...prev };
      delete next[tmpId];
      return next;
    });
    setForm((f) => ({ ...f, items: f.items.filter((it) => it._tmpId !== tmpId) }));
  };

  const moveItem = (tmpId, dir) => {
    setForm((f) => {
      const idx = f.items.findIndex((it) => it._tmpId === tmpId);
      if (idx < 0) return f;
      const next = [...f.items];
      if (dir === 'up' && idx > 0) [next[idx - 1], next[idx]] = [next[idx], next[idx - 1]];
      if (dir === 'down' && idx < next.length - 1) [next[idx], next[idx + 1]] = [next[idx + 1], next[idx]];
      return { ...f, items: next };
    });
  };

  /** Normalize to simple line: amount = rate with qty 1, no GST in editor. */
  const setLineAmountRs = (tmpId, val) => {
    const n = typeof val === 'number' && Number.isFinite(val) ? val : parseFloat(String(val)) || 0;
    setForm((f) => ({
      ...f,
      items: f.items.map((it) =>
        it._tmpId === tmpId ? { ...it, qty: 1, discount: 0, gst: 0, rate: Math.round(n * 100) / 100 } : it
      ),
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        ...form,
        invoiceType,
        client: clientId,
        items: form.items.map(({ _tmpId, ...rest }) => {
          const row = { ...rest };
          if (row.item && typeof row.item === 'object') row.item = row.item._id || row.item.id || '';
          if (!row.item) delete row.item;
          return row;
        }),
        remarks: form.remarks,
      };
      if (!payload.bankAccount) delete payload.bankAccount;
      if (!payload.project) delete payload.project;
      if (invoiceId) await api.put('/clients/' + clientId + '/invoices/' + invoiceId, payload);
      else await api.post('/clients/' + clientId + '/invoices', payload);
      const lineDesc = form.items.map((it) => String(it.itemName || '').trim()).filter(Boolean);
      const mergedHints = mergeDedupeDescriptions(readInvoiceDescHintsLS(), lineDesc);
      writeInvoiceDescHintsLS(mergedHints);
      onSaved();
      onClose();
    } catch (e) {
      alert(e.response?.data?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const iS = { padding: '6px 10px', border: '1px solid #E5E7EB', borderRadius: 8, fontSize: 13, outline: 'none', background: '#F9FAFB', fontFamily: 'inherit' };
  const thS = { padding: '8px 10px', fontSize: 11, fontWeight: 600, color: '#6B7280', borderBottom: '2px solid #E5E7EB', textAlign: 'left', background: '#fff', position: 'sticky', top: 0, zIndex: 1 };
  const tdS = { padding: '5px 8px', borderBottom: '1px solid #F3F4F6', fontSize: 12, verticalAlign: 'middle' };
  return (
    <div style={{ position: 'fixed', inset: 0, background: '#fff', zIndex: 100, overflow: 'auto', fontFamily: 'inherit' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 24px', borderBottom: '1px solid #E5E7EB', position: 'sticky', top: 0, background: '#fff', zIndex: 50 }}>
        <h2 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: '#374151' }}>Edit Client Invoice</h2>
        <div style={{ display: 'flex', gap: 8 }}>
          <button
            onClick={handleSave}
            disabled={saving}
            style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '7px 18px', background: '#976793', color: '#fff', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}
          >
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="20 6 9 17 4 12" /></svg>
            {saving ? 'Saving…' : 'Save'}
          </button>
          <button onClick={onClose} style={{ padding: '7px 18px', border: '1px solid #E5E7EB', borderRadius: 8, background: '#fff', fontSize: 13, cursor: 'pointer', color: '#374151' }}>Close</button>
        </div>
      </div>

      <div style={{ padding: '20px 24px' }}>
        {/* Top fields */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16, marginBottom: 20 }}>
          <div>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#6B7280', marginBottom: 5 }}>Title</label>
            <input style={{ ...iS, width: '100%' }} value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} placeholder="Invoice title" />
          </div>
          <div>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#6B7280', marginBottom: 5 }}>Invoice Number</label>
            <input style={{ ...iS, width: '100%', background: '#F3F4F6', color: '#9CA3AF' }} value={invoiceNumber || 'Auto: TSI-YY/MM/0001'} readOnly />
          </div>
          <div>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#6B7280', marginBottom: 5 }}>Invoice Date</label>
            <input type="date" style={{ ...iS, width: '100%' }} value={form.invoiceDate} onChange={e => setForm(f => ({ ...f, invoiceDate: e.target.value }))} />
            {!form.invoiceDate && <div style={{ color: '#EF4444', fontSize: 10, marginTop: 2 }}>This is a required field.</div>}
          </div>
        </div>

        {/* Items — simple invoice lines (Sr.No., Description, Amount) */}
        <div style={{ overflowX: 'auto', border: '1px solid #E5E7EB', borderRadius: 8, marginBottom: 12 }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 420 }}>
            <thead>
              <tr>
                <th style={{ ...thS, width: 56 }}>Sr.No.</th>
                <th style={thS}>Description</th>
                <th style={{ ...thS, textAlign: 'right', width: 120 }}>Amount (Rs.)</th>
                <th style={{ ...thS, width: 44 }}> </th>
              </tr>
              <tr style={{ background: '#F9FAFB' }}>
                <th style={{ ...thS, paddingTop: 4, paddingBottom: 8 }} />
                <th style={{ ...thS, paddingTop: 4, paddingBottom: 8, verticalAlign: 'top' }}>
                  <ColumnFilterInput value={lineColFilters.item} onChange={(e) => setLineColFilters((f) => ({ ...f, item: e.target.value }))} placeholder="Filter description…" aria-label="Filter by description" />
                </th>
                <th style={{ ...thS, paddingTop: 4, paddingBottom: 8 }} />
                <th style={{ ...thS, paddingTop: 4, paddingBottom: 8 }} />
              </tr>
            </thead>
            <tbody>
              {form.items.length === 0 ? (
                <tr>
                  <td colSpan={4} style={{ textAlign: 'center', padding: '2rem', color: '#9CA3AF', fontSize: 12 }}>No lines — use “+ Add line” below</td>
                </tr>
              ) : displayedEditorItems.length === 0 ? (
                <tr>
                  <td colSpan={4} style={{ textAlign: 'center', padding: '2rem', color: '#9CA3AF', fontSize: 12 }}>No lines match filter</td>
                </tr>
              ) : displayedEditorItems.map((item) => {
                const sr = form.items.findIndex((x) => x._tmpId === item._tmpId) + 1;
                const amt = calcAmount(item);
                const draft = amountDraftByTmpId[item._tmpId];
                const amountInputValue = draft !== undefined ? draft : (amt === 0 ? '' : String(amt));
                return (
                  <tr key={item._tmpId}>
                    <td style={{ ...tdS, color: '#6B7280', fontSize: 11, textAlign: 'center' }}>{sr}</td>
                    <td style={{ ...tdS, minWidth: 220 }}>
                      <SearchableSelectWithAdd
                        value={item.itemName || ''}
                        onChange={(v) => updateItem(item._tmpId, 'itemName', v)}
                        options={descriptionHintOptions}
                        onAddNew={addDescriptionHint}
                        addPrompt="to description suggestions"
                        placeholder="Description"
                        allowEmptyCommit
                        style={{ width: '100%', minWidth: 180 }}
                      />
                    </td>
                    <td style={{ ...tdS, textAlign: 'right' }}>
                      <input
                        type="text"
                        inputMode="decimal"
                        autoComplete="off"
                        value={amountInputValue}
                        onChange={(e) => {
                          const v = e.target.value.replace(/,/g, '');
                          if (v !== '' && !/^\d*\.?\d*$/.test(v)) return;
                          setAmountDraftByTmpId((prev) => ({ ...prev, [item._tmpId]: v }));
                          const n = v === '' || v === '.' ? 0 : parseFloat(v);
                          setLineAmountRs(item._tmpId, Number.isFinite(n) ? n : 0);
                        }}
                        onBlur={(e) => {
                          const v = e.target.value.trim().replace(/,/g, '');
                          const n = v === '' || v === '.' ? 0 : parseFloat(v);
                          setLineAmountRs(item._tmpId, Number.isFinite(n) ? n : 0);
                          setAmountDraftByTmpId((prev) => {
                            if (!(item._tmpId in prev)) return prev;
                            const next = { ...prev };
                            delete next[item._tmpId];
                            return next;
                          });
                        }}
                        style={{ width: '100%', maxWidth: 120, padding: '6px 8px', border: '1px solid #E5E7EB', borderRadius: 6, fontSize: 12, outline: 'none', fontFamily: 'inherit', textAlign: 'right' }}
                      />
                    </td>
                    <td style={{ ...tdS, width: 36, position: 'relative' }}>
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); setOpenItemMenu((m) => (m === item._tmpId ? null : item._tmpId)); }}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9CA3AF', padding: 2 }}
                      >
                        ⋮
                      </button>
                      {openItemMenu === item._tmpId && (
                        <div
                          style={{ position: 'absolute', right: 0, top: 28, background: '#fff', border: '1px solid #E5E7EB', borderRadius: 8, boxShadow: '0 4px 20px rgba(0,0,0,0.1)', zIndex: 100, overflow: 'hidden', minWidth: 120 }}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <button
                            type="button"
                            onClick={() => { removeItem(item._tmpId); setOpenItemMenu(null); }}
                            style={{ display: 'block', width: '100%', textAlign: 'left', padding: '7px 12px', border: 'none', background: 'transparent', fontSize: 11, cursor: 'pointer', color: '#EF4444' }}
                            onMouseEnter={(e) => { e.currentTarget.style.background = '#F9FAFB'; }}
                            onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                          >
                            Remove
                          </button>
                          <button
                            type="button"
                            onClick={() => { moveItem(item._tmpId, 'up'); setOpenItemMenu(null); }}
                            style={{ display: 'block', width: '100%', textAlign: 'left', padding: '7px 12px', border: 'none', background: 'transparent', fontSize: 11, cursor: 'pointer', color: '#374151' }}
                            onMouseEnter={(e) => { e.currentTarget.style.background = '#F9FAFB'; }}
                            onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                          >
                            Move up
                          </button>
                          <button
                            type="button"
                            onClick={() => { moveItem(item._tmpId, 'down'); setOpenItemMenu(null); }}
                            style={{ display: 'block', width: '100%', textAlign: 'left', padding: '7px 12px', border: 'none', background: 'transparent', fontSize: 11, cursor: 'pointer', color: '#374151' }}
                            onMouseEnter={(e) => { e.currentTarget.style.background = '#F9FAFB'; }}
                            onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                          >
                            Move down
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
          <button
            type="button"
            onClick={() => addSimpleLine()}
            style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '7px 14px', background: '#976793', color: '#fff', border: 'none', borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}
          >
            + Add line
          </button>
        </div>

        {/* Totals — GST omitted in UI (still computed on save if legacy lines had GST) */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
          <div style={{ textAlign: 'right', fontSize: 12, color: '#6B7280', minWidth: 200 }}>
            <div style={{ marginBottom: 2 }}>Sub Total: <strong>{'Rs.' + formatInr(subTotal)}</strong></div>
            <div style={{ fontWeight: 700, fontSize: 14, color: '#1F2937', marginBottom: 4 }}>{'Total: Rs.' + formatInr(total)}</div>
          </div>
        </div>

      </div>
    </div>
  );
}

// ── Add Vendor Invoice Modal ───────────────────────────────────────────────────
function AddVendorInvoiceModal({ clientId, vendors, orders, onClose, onSaved }) {
  const [form, setForm] = useState({ invoiceNumber: '', invoiceDate: '', vendor: '', order: '', amount: '', gst: 0, remarks: '' });
  const [saving, setSaving] = useState(false);

  const iS = { width: '100%', padding: '8px 12px', border: '1px solid #E5E7EB', borderRadius: 8, fontSize: 13, outline: 'none', background: '#F9FAFB', fontFamily: 'inherit' };
  const lS = { display: 'block', fontSize: 11, fontWeight: 600, color: '#6B7280', marginBottom: 4 };
  const amtWithGst = (parseFloat(form.amount) || 0) * (1 + (parseFloat(form.gst) || 0) / 100);
  const refId = (v) => (v && typeof v === 'object' ? (v._id || v.id || '') : (v || ''));
  const ordersForSelectedVendor = form.vendor ? orders.filter((o) => refId(o.vendor) === form.vendor) : orders;

  useEffect(() => {
    if (form.order) return;
    const next = form.vendor
      ? orders.find((o) => refId(o.vendor) === form.vendor)
      : (orders.length === 1 ? orders[0] : null);
    if (next?._id) setForm((f) => ({ ...f, order: next._id }));
  }, [form.vendor, form.order, orders]);

  const handleVendorChange = (vendorId) => {
    const nextOrder = vendorId ? orders.find((o) => refId(o.vendor) === vendorId) : (orders.length === 1 ? orders[0] : null);
    setForm((f) => ({ ...f, vendor: vendorId, order: nextOrder?._id || '' }));
  };

  const handleOrderChange = (orderId) => {
    const selectedOrder = orders.find((o) => o._id === orderId);
    setForm((f) => ({ ...f, order: orderId, vendor: f.vendor || refId(selectedOrder?.vendor) }));
  };

  const submit = async e => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post('/clients/' + clientId + '/invoices/vendor', form);
      onSaved();
      onClose();
    } catch (err) {
      alert(err.response?.data?.message || 'Failed');
      setSaving(false);
    }
  };

  return (
    <div
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16 }}
      onClick={onClose}
    >
      <div
        style={{ background: '#fff', borderRadius: 12, width: '100%', maxWidth: 480, boxShadow: '0 20px 60px rgba(0,0,0,0.15)' }}
        onClick={e => e.stopPropagation()}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 20px', borderBottom: '1px solid #E5E7EB' }}>
          <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700 }}>Add Vendor Invoice</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 18, cursor: 'pointer', color: '#9CA3AF' }}>×</button>
        </div>
        <form onSubmit={submit} style={{ padding: '16px 20px' }}>
          <div style={{ marginBottom: 12 }}>
            <label style={lS}>Invoice Number</label>
            <input style={{ ...iS, background: '#F3F4F6', color: '#9CA3AF' }} value={form.invoiceNumber || 'Auto: TSI-YY/MM/0001'} readOnly />
          </div>
          <div style={{ marginBottom: 12 }}>
            <label style={lS}>Invoice Date *</label>
            <input type="date" style={iS} value={form.invoiceDate} onChange={e => setForm(f => ({ ...f, invoiceDate: e.target.value }))} required />
          </div>
          <div style={{ marginBottom: 12 }}>
            <label style={lS}>Vendor</label>
            <select style={iS} value={form.vendor} onChange={e => handleVendorChange(e.target.value)}>
              <option value="">Select</option>
              {vendors.map(v => <option key={v._id} value={v._id}>{v.displayName}</option>)}
            </select>
          </div>
          <div style={{ marginBottom: 12 }}>
            <label style={lS}>Order ID</label>
            <select style={iS} value={form.order} onChange={e => handleOrderChange(e.target.value)}>
              <option value="">Select</option>
              {ordersForSelectedVendor.map(o => <option key={o._id} value={o._id}>{o.orderCode}</option>)}
            </select>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10, marginBottom: 12 }}>
            <div>
              <label style={lS}>Amount *</label>
              <input style={iS} type="number" value={form.amount} onChange={e => setForm(f => ({ ...f, amount: e.target.value }))} required />
            </div>
            <div>
              <label style={lS}>GST (%)</label>
              <select style={iS} value={form.gst} onChange={e => setForm(f => ({ ...f, gst: e.target.value }))}>
                <option value="">Select</option>
                {[0, 5, 12, 18, 28].map(g => <option key={g} value={g}>{g}%</option>)}
              </select>
            </div>
            <div>
              <label style={lS}>Amount with GST</label>
              <div style={{ padding: '8px 12px', border: '1px solid #E5E7EB', borderRadius: 8, fontSize: 13, background: '#F3F4F6', color: '#6B7280' }}>
                {'= Rs. ' + formatInr(amtWithGst)}
              </div>
            </div>
          </div>
          <div style={{ marginBottom: 12 }}>
            <label style={lS}>Remarks</label>
            <input style={iS} value={form.remarks} onChange={e => setForm(f => ({ ...f, remarks: e.target.value }))} />
          </div>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', borderTop: '1px solid #F3F4F6', paddingTop: 14 }}>
            <button type="button" onClick={onClose} style={{ padding: '7px 18px', border: '1px solid #E5E7EB', borderRadius: 8, background: '#fff', fontSize: 13, cursor: 'pointer' }}>Cancel</button>
            <button type="submit" disabled={saving} style={{ padding: '7px 18px', background: '#976793', color: '#fff', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
              {saving ? 'Saving…' : 'Submit'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── Main InvoicesTab ──────────────────────────────────────────────────────────
export default function InvoicesTab({ clientId, client, project }) {
  const [activeView, setActiveView] = useState('clients');
  const [invoices, setInvoices] = useState([]);
  const [vendorInvoices, setVendorInvoices] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [orders, setOrders] = useState([]);
  const [latestMilestoneQuote, setLatestMilestoneQuote] = useState(null);
  const [paymentReceipts, setPaymentReceipts] = useState([]);
  const [receivedDrafts, setReceivedDrafts] = useState({});
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);
  const [showEditor, setShowEditor] = useState(false);
  const [editInvoiceId, setEditInvoiceId] = useState(null);
  const [editInvoiceType, setEditInvoiceType] = useState('INV');
  const [showVendorModal, setShowVendorModal] = useState(false);
  const [openRowMenu, setOpenRowMenu] = useState(null);
  const [invoiceListSearch, setInvoiceListSearch] = useState('');
  const [invoiceLineFiltersById, setInvoiceLineFiltersById] = useState({});
  const [vendorInvoiceSearch, setVendorInvoiceSearch] = useState('');
  const [whatsAppLoading, setWhatsAppLoading] = useState(null);

  const emptyClientInvoiceLineFilters = () => ({ item: '' });
  const patchInvoiceLineFilters = (invId, patch) => {
    setInvoiceLineFiltersById((prev) => ({
      ...prev,
      [invId]: { ...emptyClientInvoiceLineFilters(), ...(prev[invId] || {}), ...patch },
    }));
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const fresh = Date.now();
      const invoiceParams = project?._id ? { project: project._id, fresh } : { fresh };
      const [iR, vR, vdR, oR, qR, receiptR] = await Promise.all([
        api.get('/clients/' + clientId + '/invoices', { params: invoiceParams }),
        api.get('/clients/' + clientId + '/invoices/vendor/all', { params: { fresh } }),
        api.get('/vendors', { params: { limit: 200, fresh } }),
        api.get('/orders', { params: { client: clientId, limit: 100, fresh } }),
        project?._id ? api.get('/quotes', { params: { client: clientId, project: project._id, limit: 100, page: 1, fresh } }) : Promise.resolve(null),
        project?._id ? api.get(`/clients/${clientId}/projects/${project._id}/payment-receipts`, { params: { fresh } }) : Promise.resolve(null),
      ]);
      setInvoices(iR.data.data.invoices || []);
      setVendorInvoices(vR.data.data.items || []);
      setVendors(vdR.data.data.vendors || []);
      setOrders(oR.data.data.orders || []);
      const quoteList = qR?.data?.data?.quotes || [];
      const sortedQuotes = quoteList
        .filter((q) => q && q.status !== 'rejected' && Number(q.total) > 0)
        .sort((a, b) => new Date(b.updatedAt || b.createdAt || 0) - new Date(a.updatedAt || a.createdAt || 0));
      setLatestMilestoneQuote(sortedQuotes[0] || null);
      const receipts = receiptR?.data?.data?.items || [];
      setPaymentReceipts(receipts);
      setReceivedDrafts(
        receipts.reduce((acc, rec) => {
          if (rec?.milestonePct != null) acc[String(rec.milestonePct)] = String(Number(rec.amount) || 0);
          return acc;
        }, {})
      );
    } catch (e) {
      console.error(e);
      setLatestMilestoneQuote(null);
      setPaymentReceipts([]);
    } finally {
      setLoading(false);
    }
  }, [clientId, project?._id]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const close = () => setOpenRowMenu(null);
    document.addEventListener('click', close);
    return () => document.removeEventListener('click', close);
  }, []);

  const openEditor = (id = null, type = 'INV') => {
    setEditInvoiceId(id);
    setEditInvoiceType(type);
    setShowEditor(true);
  };

  const handleApprove = async (id) => {
    try {
      await api.patch('/clients/' + clientId + '/invoices/' + id + '/status', { status: 'approved' });
      load();
    } catch (e) {
      alert('Failed to approve');
    }
  };

  const handleReject = async (id) => {
    if (!window.confirm('Reject this invoice?')) return;
    try {
      await api.patch('/clients/' + clientId + '/invoices/' + id + '/status', { status: 'rejected' });
      load();
    } catch (e) {
      alert('Failed to reject');
    }
  };

  const handleDeleteInvoice = async (id, number) => {
    if (!window.confirm(`Delete invoice ${number || id}? This cannot be undone.`)) return;
    try {
      await api.delete('/clients/' + clientId + '/invoices/' + id);
      setOpenRowMenu(null);
      if (expanded === id) setExpanded(null);
      load();
    } catch (e) {
      alert(e.response?.data?.message || 'Delete failed');
    }
  };

  const handleMarkPaid = async (id, invoiceTotal) => {
    if (!window.confirm('Mark this invoice as paid?')) return;
    try {
      await api.patch('/clients/' + clientId + '/invoices/' + id + '/status', { status: 'paid' });
      const t = Number(invoiceTotal);
      if (Number.isFinite(t) && t >= 0) {
        try {
          await api.patch('/clients/' + clientId + '/invoices/' + id + '/paid-details', { paidAmount: t });
        } catch (_) {
          /* paid-details optional if older backend */
        }
      }
      load();
    } catch (e) {
      alert(e.response?.data?.message || 'Failed to mark paid');
    }
  };

  const handleDuplicate = async (inv) => {
    try {
      await api.post('/clients/' + clientId + '/invoices', {
        title: 'Copy of ' + (inv.title || inv.invoiceNumber),
        invoiceType: inv.invoiceType,
        project: inv.project?._id || inv.project || project?._id || undefined,
        items: inv.items.map(({ _id, ...rest }) => rest),
        charges: inv.charges,
      });
      load();
    } catch (e) {
      alert('Failed to duplicate');
    }
  };

  const downloadCSV = (inv) => {
    if (!inv.items?.length) return;
    const rows = [
      ['Sr.No.', 'Description', 'Amount (Rs.)'],
      ...(inv.items || []).map((it, i) => {
        const desc = [it.itemName, it.itemDetails].filter(Boolean).join(' ').replace(/\r?\n/g, ' ');
        return [i + 1, desc, it.amount != null ? it.amount : ((it.qty || 1) * (it.rate || 0))];
      })
    ];
    const csv = rows.map(r => r.map(v => '"' + v + '"').join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = (inv.invoiceNumber || 'Invoice') + '.csv';
    a.click();
  };

  const exportVendorExcel = () => {
    const rows = [
      ['Created At', 'Created By', 'Invoice No.', 'Vendor Name', 'Order ID', 'Invoice Date', 'Amount', 'Total Amount', 'GST', 'Remarks'],
      ...vendorInvoices.map(vi => [
        vi.createdAt ? new Date(vi.createdAt).toLocaleDateString('en-IN') : '',
        vi.createdBy?.name || '',
        vi.invoiceNumber || '',
        vi.vendor?.displayName || '',
        vi.order?.orderCode || '',
        vi.invoiceDate ? new Date(vi.invoiceDate).toLocaleDateString('en-IN') : '',
        vi.amount || 0,
        vi.totalAmount || 0,
        vi.gst || 0,
        vi.remarks || '',
      ])
    ];
    const csv = rows.map(r => r.map(v => '"' + v + '"').join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = 'vendor-invoices.csv';
    a.click();
  };

  const handleSendInvoiceWhatsApp = async (inv) => {
    if (!hasItems(inv)) return;
    setWhatsAppLoading(inv._id);
    try {
      const pdfBlob = await generateInvoicePDFBlob(inv, client || null, project || null);
      const formData = new FormData();
      const safeNumber = String(inv.invoiceNumber || 'Invoice').replace(/[\\/:*?"<>|]/g, '-');
      formData.append('pdf', pdfBlob, `${safeNumber}.pdf`);

      try {
        const res = await api.post(`/invoices/${inv._id}/whatsapp-pdf`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        const data = res.data?.data || {};
        const phoneDigits = String(data.phoneNumber || '').replace(/\D/g, '');
        const phoneNumber = phoneDigits.length > 10 ? phoneDigits.slice(-10) : phoneDigits;
        if (!phoneNumber || !data.pdfUrl) throw new Error('Missing WhatsApp phone number or invoice PDF link');

        const message = `Hello ${data.clientName || client?.name || 'Client'}, please find your bill attached:\n${data.pdfUrl}`;
        window.open(`https://wa.me/91${phoneNumber}?text=${encodeURIComponent(message)}`, '_blank', 'noopener,noreferrer');
        alert('Invoice PDF is ready to send on WhatsApp.');
      } catch (uploadErr) {
        const msg = uploadErr.response?.data?.message || uploadErr.message || '';
        const isMissingRoute = uploadErr.response?.status === 404 && /whatsapp-pdf|route .*not found/i.test(msg);
        if (!isMissingRoute) throw uploadErr;

        const url = URL.createObjectURL(pdfBlob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${safeNumber}.pdf`;
        a.rel = 'noopener';
        document.body.appendChild(a);
        a.click();
        a.remove();
        setTimeout(() => URL.revokeObjectURL(url), 2000);

        const phoneDigits = String(client?.phone || project?.phone || inv.client?.phone || '').replace(/\D/g, '');
        const phoneNumber = phoneDigits.length > 10 ? phoneDigits.slice(-10) : phoneDigits;
        if (!phoneNumber) throw new Error('Invoice PDF downloaded, but client phone number is missing');

        const message = `Hello ${client?.name || inv.client?.name || 'Client'}, please find your bill attached.`;
        window.open(`https://wa.me/91${phoneNumber}?text=${encodeURIComponent(message)}`, '_blank', 'noopener,noreferrer');
        alert('Invoice PDF downloaded. WhatsApp is open; please attach the downloaded PDF manually.');
      }
    } catch (e) {
      alert(e.response?.data?.message || e.message || 'Could not prepare WhatsApp message');
    } finally {
      setWhatsAppLoading(null);
    }
  };

  const hasItems = (inv) => inv.items?.length > 0;

  const filteredInvoices = useMemo(() => {
    const s = invoiceListSearch.trim();
    if (!s) return invoices;
    return invoices.filter((inv) =>
      textHaystackIncludes(
        [inv.invoiceNumber, inv.title, inv.status, inv.invoiceDate ? fmtDate(inv.invoiceDate) : ''],
        invoiceListSearch
      )
    );
  }, [invoices, invoiceListSearch]);

  const filteredVendorInvoices = useMemo(() => {
    const s = vendorInvoiceSearch.trim();
    if (!s) return vendorInvoices;
    return vendorInvoices.filter((vi) =>
      textHaystackIncludes(
        [
          vi.invoiceNumber,
          vi.vendor?.displayName,
          vi.order?.orderCode,
          vi.remarks,
          vi.createdBy?.name,
          vi.invoiceDate ? new Date(vi.invoiceDate).toLocaleDateString('en-IN') : '',
        ],
        vendorInvoiceSearch
      )
    );
  }, [vendorInvoices, vendorInvoiceSearch]);

  const thS = { padding: '8px 10px', fontSize: 11, fontWeight: 600, color: '#6B7280', borderBottom: '1px solid #E5E7EB', textAlign: 'left', background: '#F9FAFB' };
  const tdS = { padding: '8px 10px', fontSize: 12, color: '#374151', borderBottom: '1px solid #F3F4F6', verticalAlign: 'middle' };

  if (showEditor) {
    return (
      <InvoiceEditor
        invoiceId={editInvoiceId}
        clientId={clientId}
        projectId={project?._id || ''}
        invoiceType={editInvoiceType}
        onClose={() => { setShowEditor(false); setEditInvoiceId(null); }}
        onSaved={() => { load(); setShowEditor(false); setEditInvoiceId(null); }}
      />
    );
  }

  return (
    <div>
      {/* View toggle + Actions */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16, flexWrap: 'wrap', gap: 10 }}>
        <div style={{ display: 'flex', gap: 4 }}>
          <button
            onClick={() => setActiveView('clients')}
            style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '6px 14px', borderRadius: 8, border: 'none', fontSize: 12, fontWeight: 500, cursor: 'pointer', background: activeView === 'clients' ? '#F7F1F7' : '#F3F4F6', color: activeView === 'clients' ? '#976793' : '#374151' }}
          >
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="8" r="4" /><path d="M4 20c0-4 3.6-7 8-7s8 3 8 7" /></svg>
            Clients
          </button>
          <button
            onClick={() => setActiveView('vendors')}
            style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '6px 14px', borderRadius: 8, border: 'none', fontSize: 12, fontWeight: 500, cursor: 'pointer', background: activeView === 'vendors' ? '#F7F1F7' : '#F3F4F6', color: activeView === 'vendors' ? '#976793' : '#374151' }}
          >
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" /></svg>
            Vendors
          </button>
        </div>
        {activeView === 'clients' && (
          <div style={{ display: 'flex', gap: 8 }}>
            <button type="button" onClick={() => openEditor(null, 'INV')} style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '7px 14px', background: '#976793', color: '#fff', border: 'none', borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>+ Add Invoice</button>
            <button style={{ width: 30, height: 30, border: '1px solid #E5E7EB', borderRadius: 6, background: '#fff', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#9CA3AF' }}>⋮</button>
          </div>
        )}
        {activeView === 'vendors' && (
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={() => setShowVendorModal(true)} style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '7px 14px', background: '#976793', color: '#fff', border: 'none', borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>+ Add Vendor Invoice</button>
            <button onClick={exportVendorExcel} style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '7px 14px', border: '1px solid #E5E7EB', borderRadius: 8, background: '#fff', fontSize: 12, cursor: 'pointer', color: '#374151' }}>
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="7 10 12 15 17 10" /><line x1="12" y1="15" x2="12" y2="3" /></svg>
              Export
            </button>
          </div>
        )}
      </div>

      {/* Clients View */}
      {activeView === 'clients' && (
        <div>
          {project?._id && (
            <InvoiceMilestoneSection
              clientId={clientId}
              project={project}
              client={client}
              latestQuote={latestMilestoneQuote}
              paymentReceipts={paymentReceipts}
              receivedDrafts={receivedDrafts}
              setReceivedDrafts={setReceivedDrafts}
              onSaved={load}
            />
          )}
          {loading ? (
            <div style={{ textAlign: 'center', padding: '3rem', color: '#9CA3AF' }}>Loading…</div>
          ) : invoices.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '4rem', color: '#9CA3AF', border: '1px solid #E5E7EB', borderRadius: 10 }}>No invoices yet</div>
          ) : (
            <>
          <TableSearchInput
            value={invoiceListSearch}
            onChange={(e) => setInvoiceListSearch(e.target.value)}
            placeholder="Search invoices (number, title, status…)"
            style={{ maxWidth: '100%', marginBottom: 12 }}
          />
          {filteredInvoices.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '3rem', color: '#9CA3AF', border: '1px solid #E5E7EB', borderRadius: 10, fontSize: 13 }}>No invoices match your search</div>
          ) : filteredInvoices.map(inv => (
            <div key={inv._id} style={{ border: '1px solid #E5E7EB', borderRadius: 10, marginBottom: 10, overflow: 'hidden' }}>
              {/* Row header */}
              <div
                style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '11px 14px', background: '#fff', cursor: 'pointer' }}
                onClick={() => setExpanded(e => e === inv._id ? null : inv._id)}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                  <div style={{ width: 28, height: 28, borderRadius: 6, background: '#FFF7ED', border: '1px solid #FED7AA', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#F97316" strokeWidth="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" /></svg>
                  </div>
                  <span style={{ fontSize: 13, fontWeight: 700, color: '#1F2937' }}>{inv.invoiceNumber}</span>
                  {inv.title && (
                    <span style={{ background: '#F3F4F6', color: '#374151', padding: '1px 8px', borderRadius: 20, fontSize: 10, fontWeight: 600 }}>{inv.title}</span>
                  )}
                  <span style={{
                    background: inv.status === 'paid' ? '#DBEAFE' : inv.status === 'approved' ? '#D1FAE5' : inv.status === 'rejected' ? '#FEE2E2' : '#F3F4F6',
                    color: inv.status === 'paid' ? '#976793' : inv.status === 'approved' ? '#059669' : inv.status === 'rejected' ? '#EF4444' : '#6B7280',
                    padding: '2px 9px', borderRadius: 20, fontSize: 10, fontWeight: 600, textTransform: 'capitalize'
                  }}>
                    {inv.status}
                  </span>
                  {inv.invoiceDate && (
                    <span style={{ color: '#976793', fontSize: 11, fontWeight: 500 }}>{fmtDate(inv.invoiceDate)}</span>
                  )}
                  <button style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9CA3AF' }}>📋</button>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ fontSize: 13, fontWeight: 700, color: '#1F2937' }}>
                    {inv.total > 0 ? 'Rs. ' + formatInr(inv.total) : 'Rs. ' + formatInr(0)}
                  </span>
                  <span style={{ color: '#9CA3AF', fontSize: 14 }}>{expanded === inv._id ? '▲' : '▼'}</span>
                </div>
              </div>
              <div style={{ fontSize: 11, color: '#9CA3AF', padding: '0 14px 8px' }}>
                Last updated: {fmt(inv.updatedAt)}
              </div>

              {/* Expanded content */}
              {expanded === inv._id && (
                <div style={{ borderTop: '1px solid #E5E7EB' }}>
                  {/* Action buttons */}
                  <div style={{ display: 'flex', gap: 8, padding: '10px 14px', background: '#FAFAFA', borderBottom: '1px solid #E5E7EB', justifyContent: 'flex-end' }}>
                    <button
                      onClick={() => openEditor(inv._id, inv.invoiceType)}
                      style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '5px 12px', border: '1px solid #976793', borderRadius: 8, background: '#fff', color: '#976793', fontSize: 11, fontWeight: 600, cursor: 'pointer' }}
                    >
                      ✏ Edit
                    </button>
                    <button
                      onClick={() => hasItems(inv) && inv.status !== 'approved' && inv.status !== 'paid' && handleApprove(inv._id)}
                      disabled={!hasItems(inv) || inv.status === 'approved' || inv.status === 'paid'}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 4, padding: '5px 12px',
                        border: '1px solid ' + (hasItems(inv) && inv.status !== 'approved' && inv.status !== 'paid' ? '#10B981' : '#D1D5DB'),
                        borderRadius: 8, background: '#fff',
                        color: hasItems(inv) && inv.status !== 'approved' && inv.status !== 'paid' ? '#10B981' : '#9CA3AF',
                        fontSize: 11, fontWeight: 600,
                        cursor: hasItems(inv) && inv.status !== 'approved' && inv.status !== 'paid' ? 'pointer' : 'not-allowed',
                        opacity: hasItems(inv) ? 1 : 0.5,
                      }}
                    >
                      ✓ Approve
                    </button>
                    <button
                      onClick={() => hasItems(inv) && inv.status === 'approved' && handleMarkPaid(inv._id, inv.total)}
                      disabled={!hasItems(inv) || inv.status !== 'approved'}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 4, padding: '5px 12px',
                        border: '1px solid ' + (hasItems(inv) && inv.status === 'approved' ? '#976793' : '#D1D5DB'),
                        borderRadius: 8, background: '#fff',
                        color: hasItems(inv) && inv.status === 'approved' ? '#976793' : '#9CA3AF',
                        fontSize: 11, fontWeight: 600,
                        cursor: hasItems(inv) && inv.status === 'approved' ? 'pointer' : 'not-allowed',
                      }}
                    >
                      ₹ Mark paid
                    </button>
                    <button
                      onClick={() => {
                        if (!hasItems(inv)) return;
                        generateInvoicePDF(inv, client || null, project || null).catch((e) => alert(e?.message || 'PDF failed'));
                      }}
                      disabled={!hasItems(inv)}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 4, padding: '5px 12px',
                        border: '1px solid ' + (hasItems(inv) ? '#EF4444' : '#D1D5DB'),
                        borderRadius: 8, background: '#fff',
                        color: hasItems(inv) ? '#EF4444' : '#9CA3AF',
                        fontSize: 11, fontWeight: 600,
                        cursor: hasItems(inv) ? 'pointer' : 'not-allowed',
                        opacity: hasItems(inv) ? 1 : 0.5,
                      }}
                    >
                      📄 PDF
                    </button>
                    <button
                      onClick={() => handleSendInvoiceWhatsApp(inv)}
                      disabled={!hasItems(inv) || whatsAppLoading === inv._id}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 4, padding: '5px 12px',
                        border: '1px solid ' + (hasItems(inv) ? '#16A34A' : '#D1D5DB'),
                        borderRadius: 8, background: '#fff',
                        color: hasItems(inv) ? '#16A34A' : '#9CA3AF',
                        fontSize: 11, fontWeight: 600,
                        cursor: hasItems(inv) && whatsAppLoading !== inv._id ? 'pointer' : 'not-allowed',
                        opacity: hasItems(inv) ? 1 : 0.5,
                      }}
                    >
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M12.04 2C6.58 2 2.13 6.45 2.13 11.91c0 1.75.46 3.45 1.32 4.95L2 22l5.28-1.38a9.87 9.87 0 0 0 4.76 1.21h.01c5.46 0 9.91-4.45 9.91-9.91C21.96 6.45 17.51 2 12.04 2Zm0 18.16h-.01a8.2 8.2 0 0 1-4.18-1.14l-.3-.18-3.13.82.84-3.05-.2-.31a8.18 8.18 0 0 1-1.25-4.39c0-4.54 3.69-8.23 8.24-8.23 2.2 0 4.27.86 5.82 2.41a8.17 8.17 0 0 1 2.41 5.82c0 4.54-3.7 8.25-8.24 8.25Zm4.51-6.17c-.25-.12-1.46-.72-1.69-.8-.23-.08-.39-.12-.56.12-.17.25-.64.8-.79.96-.14.17-.29.19-.54.06-.25-.12-1.04-.38-1.98-1.22-.73-.65-1.23-1.46-1.37-1.71-.14-.25-.02-.38.11-.5.11-.11.25-.29.37-.43.12-.14.17-.25.25-.41.08-.17.04-.31-.02-.43-.06-.12-.56-1.35-.77-1.85-.2-.49-.41-.42-.56-.43h-.48c-.17 0-.43.06-.66.31-.23.25-.86.84-.86 2.04s.89 2.37 1.02 2.53c.12.17 1.76 2.69 4.26 3.77.6.26 1.06.41 1.42.53.6.19 1.14.16 1.57.1.48-.07 1.46-.6 1.67-1.18.21-.58.21-1.08.14-1.18-.06-.1-.23-.16-.48-.28Z"/></svg>
                      {whatsAppLoading === inv._id ? 'Preparing...' : 'WhatsApp'}
                    </button>
                    <div style={{ position: 'relative' }}>
                      <button
                        onClick={e => { e.stopPropagation(); setOpenRowMenu(m => m === inv._id ? null : inv._id); }}
                        style={{ width: 28, height: 28, border: '1px solid #E5E7EB', borderRadius: 6, background: '#fff', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#9CA3AF' }}
                      >
                        ⋮
                      </button>
                      {openRowMenu === inv._id && (
                        <div
                          style={{ position: 'absolute', right: 0, top: 32, background: '#fff', border: '1px solid #E5E7EB', borderRadius: 8, boxShadow: '0 4px 20px rgba(0,0,0,0.1)', zIndex: 100, overflow: 'hidden', minWidth: 160 }}
                          onClick={e => e.stopPropagation()}
                        >
                          {hasItems(inv) && (
                            <button
                              onClick={() => { downloadCSV(inv); setOpenRowMenu(null); }}
                              style={{ display: 'block', width: '100%', textAlign: 'left', padding: '8px 14px', border: 'none', background: 'transparent', fontSize: 12, cursor: 'pointer', color: '#374151' }}
                              onMouseEnter={e => e.currentTarget.style.background = '#F9FAFB'}
                              onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                            >
                              📊 Download CSV
                            </button>
                          )}
                          {inv.status !== 'paid' && (
                          <button
                            onClick={() => { handleReject(inv._id); setOpenRowMenu(null); }}
                            style={{ display: 'block', width: '100%', textAlign: 'left', padding: '8px 14px', border: 'none', background: 'transparent', fontSize: 12, cursor: 'pointer', color: '#EF4444' }}
                            onMouseEnter={e => e.currentTarget.style.background = '#FEF2F2'}
                            onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                          >
                            Reject
                          </button>
                          )}
                          <button
                            onClick={() => { handleDuplicate(inv); setOpenRowMenu(null); }}
                            style={{ display: 'block', width: '100%', textAlign: 'left', padding: '8px 14px', border: 'none', background: 'transparent', fontSize: 12, cursor: 'pointer', color: '#374151' }}
                            onMouseEnter={e => e.currentTarget.style.background = '#F9FAFB'}
                            onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                          >
                            Create Duplicate
                          </button>
                          <button
                            onClick={() => handleDeleteInvoice(inv._id, inv.invoiceNumber)}
                            style={{ display: 'block', width: '100%', textAlign: 'left', padding: '8px 14px', border: 'none', background: 'transparent', fontSize: 12, cursor: 'pointer', color: '#EF4444' }}
                            onMouseEnter={e => e.currentTarget.style.background = '#FEF2F2'}
                            onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                          >
                            Delete
                          </button>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Items table */}
                  <div style={{ padding: '0 14px 14px', overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: 10, minWidth: 360 }}>
                      <thead>
                        <tr>
                          {['Sr.No.', 'Description', 'Amount (Rs.)'].map((h) => (
                            <th key={h} style={h === 'Amount (Rs.)' ? { ...thS, textAlign: 'right' } : thS}>
                              {h}
                            </th>
                          ))}
                        </tr>
                        <tr style={{ background: '#FAFAFA' }}>
                          <th style={{ ...thS, paddingTop: 4, paddingBottom: 8 }} />
                          <th style={{ ...thS, paddingTop: 4, paddingBottom: 8, verticalAlign: 'top' }}>
                            {(() => {
                              const lf = invoiceLineFiltersById[inv._id] || emptyClientInvoiceLineFilters();
                              return <ColumnFilterInput value={lf.item} onChange={(e) => patchInvoiceLineFilters(inv._id, { item: e.target.value })} placeholder="Filter description…" aria-label="Filter by description" />;
                            })()}
                          </th>
                          <th style={{ ...thS, paddingTop: 4, paddingBottom: 8 }} />
                        </tr>
                      </thead>
                      <tbody>
                        {(() => {
                          const lf = invoiceLineFiltersById[inv._id] || emptyClientInvoiceLineFilters();
                          const all = inv.items || [];
                          const rows = all.filter((it) => matchesInvoiceEditorLine(it, lf));
                          if (all.length === 0) {
                            return <tr><td colSpan={3} style={{ textAlign: 'center', padding: '1.5rem', color: '#9CA3AF', fontSize: 12 }}>No data available</td></tr>;
                          }
                          if (rows.length === 0) {
                            return <tr><td colSpan={3} style={{ textAlign: 'center', padding: '1.5rem', color: '#9CA3AF', fontSize: 12 }}>No lines match filters</td></tr>;
                          }
                          return rows.map((it, i) => {
                            const desc = [it.itemName, it.itemDetails].filter(Boolean).join('\n') || '—';
                            const lineAmt = it.amount != null ? it.amount : ((it.qty || 1) * (it.rate || 0));
                            return (
                              <tr key={it._id || i}>
                                <td style={{ ...tdS, color: '#6B7280', fontSize: 11, width: 48 }}>{i + 1}</td>
                                <td style={{ ...tdS, fontSize: 12, whiteSpace: 'pre-wrap' }}>{desc}</td>
                                <td style={{ ...tdS, fontWeight: 600, textAlign: 'right' }}>Rs. {formatInr(lineAmt)}</td>
                              </tr>
                            );
                          });
                        })()}
                      </tbody>
                    </table>
                    {inv.total > 0 && (
                      <div style={{ textAlign: 'right', padding: '10px 0', fontSize: 12, color: '#6B7280' }}>
                        <div>Sub Total: Rs.{formatInr(inv.subTotal || 0)}</div>
                        <div style={{ fontWeight: 700, color: '#1F2937', fontSize: 13 }}>Total: Rs.{formatInr(inv.total || 0)}</div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          ))}
          <div style={{ fontSize: 12, color: '#9CA3AF', padding: '8px 0' }}>{filteredInvoices.length} shown · {invoices.length} total</div>
            </>
          )}
        </div>
      )}

      {/* Vendors View */}
      {activeView === 'vendors' && (
        <div style={{ overflowX: 'auto' }}>
          <TableSearchInput
            value={vendorInvoiceSearch}
            onChange={(e) => setVendorInvoiceSearch(e.target.value)}
            placeholder="Search vendor invoices…"
            style={{ maxWidth: '100%', marginBottom: 12 }}
          />
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 900 }}>
            <thead>
              <tr>
                {['Created At', 'Created By', 'Invoice No.', 'Vendor Name', 'Order ID', 'Invoice Date', 'Amount', 'Total Amount', 'GST', 'Remarks', 'Invoices'].map(h => (
                  <th key={h} style={thS}>
                    {h}<br />
                    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#9CA3AF" strokeWidth="2"><circle cx="11" cy="11" r="8" /><path d="M21 21l-4.35-4.35" /></svg>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={11} style={{ textAlign: 'center', padding: '3rem', color: '#9CA3AF' }}>Loading…</td></tr>
              ) : vendorInvoices.length === 0 ? (
                <tr><td colSpan={11} style={{ textAlign: 'center', padding: '3rem', color: '#9CA3AF' }}>No data available</td></tr>
              ) : filteredVendorInvoices.length === 0 ? (
                <tr><td colSpan={11} style={{ textAlign: 'center', padding: '3rem', color: '#9CA3AF', fontSize: 13 }}>No rows match your search</td></tr>
              ) : filteredVendorInvoices.map(vi => (
                <tr key={vi._id}>
                  <td style={tdS}>{vi.createdAt ? new Date(vi.createdAt).toLocaleDateString('en-IN') : '—'}</td>
                  <td style={tdS}>{vi.createdBy?.name || '—'}</td>
                  <td style={{ ...tdS, fontWeight: 600 }}>{vi.invoiceNumber || '—'}</td>
                  <td style={tdS}>{vi.vendor?.displayName || '—'}</td>
                  <td style={tdS}>{vi.order?.orderCode || '—'}</td>
                  <td style={tdS}>{vi.invoiceDate ? new Date(vi.invoiceDate).toLocaleDateString('en-IN') : '—'}</td>
                  <td style={tdS}>Rs. {formatInr(vi.amount || 0)}</td>
                  <td style={{ ...tdS, fontWeight: 600 }}>Rs. {formatInr(vi.totalAmount || 0)}</td>
                  <td style={tdS}>{vi.gst || 0}%</td>
                  <td style={tdS}>{vi.remarks || '—'}</td>
                  <td style={tdS}>—</td>
                </tr>
              ))}
            </tbody>
          </table>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 0', fontSize: 12, color: '#9CA3AF', borderTop: '1px solid #E5E7EB', marginTop: 8 }}>
            <span>{filteredVendorInvoices.length} shown · {vendorInvoices.length} total</span>
            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <button style={{ width: 26, height: 26, border: '1px solid #E5E7EB', borderRadius: 4, background: '#fff', cursor: 'pointer' }}>‹</button>
              <button style={{ width: 26, height: 26, border: '1px solid #E5E7EB', borderRadius: 4, background: '#fff', cursor: 'pointer' }}>›</button>
              <select style={{ padding: '2px 6px', border: '1px solid #E5E7EB', borderRadius: 4, fontSize: 11, outline: 'none' }}>
                <option>25</option>
                <option>50</option>
              </select>
            </div>
          </div>
        </div>
      )}

      {showVendorModal && (
        <AddVendorInvoiceModal
          clientId={clientId}
          vendors={vendors}
          orders={orders}
          onClose={() => setShowVendorModal(false)}
          onSaved={load}
        />
      )}
    </div>
  );
}
